import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { MainLayout } from '@/components/shared/MainLayout'
import { ModeSwitcherPopover } from '@/components/shared/ModeSwitcherPopover'
import { RequireAuth } from '@/components/shared/RequireAuth'
import { RequireCompany } from '@/components/shared/RequireCompany'

const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Agents = lazy(() => import('@/pages/Agents').then((m) => ({ default: m.Agents })))
const AgentDetail = lazy(() => import('@/pages/AgentDetail').then((m) => ({ default: m.AgentDetail })))
const AgentWorkspace = lazy(() => import('@/pages/AgentWorkspace').then((m) => ({ default: m.AgentWorkspace })))
const AgentRecommendations = lazy(() => import('@/pages/AgentRecommendations').then((m) => ({ default: m.AgentRecommendations })))
const Tasks = lazy(() => import('@/pages/Tasks').then((m) => ({ default: m.Tasks })))
const Goals = lazy(() => import('@/pages/Goals').then((m) => ({ default: m.Goals })))
const GoalDetail = lazy(() => import('@/pages/GoalDetail').then((m) => ({ default: m.GoalDetail })))
const InboxPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.InboxPage })))
const ConnectorSessions = lazy(() => import('@/pages/ConnectorSessions').then((m) => ({ default: m.ConnectorSessions })))
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase').then((m) => ({ default: m.KnowledgeBase })))
const Projects = lazy(() => import('@/pages/Projects').then((m) => ({ default: m.Projects })))
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail').then((m) => ({ default: m.ProjectDetail })))
const Workflow = lazy(() => import('@/pages/Workflow').then((m) => ({ default: m.Workflow })))
const WorkflowOps = lazy(() => import('@/pages/WorkflowOps').then((m) => ({ default: m.WorkflowOps })))
const Routines = lazy(() => import('@/pages/Routines').then((m) => ({ default: m.Routines })))
const RoutineEditor = lazy(() => import('@/pages/RoutineEditor').then((m) => ({ default: m.RoutineEditor })))
const RunTranscript = lazy(() => import('@/pages/RunTranscript').then((m) => ({ default: m.RunTranscript })))
const CostAnalytics = lazy(() => import('@/pages/CostAnalytics').then((m) => ({ default: m.CostAnalytics })))
const UserSettings = lazy(() => import('@/pages/UserSettings').then((m) => ({ default: m.UserSettings })))
const SipocWizard = lazy(() => import('@/pages/SipocWizard').then((m) => ({ default: m.SipocWizard })))
const SipocManagement = lazy(() => import('@/pages/SipocManagement').then((m) => ({ default: m.SipocManagement })))
const SipocSettings = lazy(() => import('@/pages/SipocSettings').then((m) => ({ default: m.SipocSettings })))
const SipocAnalytics = lazy(() => import('@/pages/SipocAnalytics').then((m) => ({ default: m.SipocAnalytics })))
const SipocReport = lazy(() => import('@/pages/SipocReport').then((m) => ({ default: m.SipocReport })))
const Org = lazy(() => import('@/pages/Org').then((m) => ({ default: m.Org })))
const PmoDashboard = lazy(() => import('@/pages/PmoDashboard').then((m) => ({ default: m.PmoDashboard })))
const Council = lazy(() => import('@/pages/Council').then((m) => ({ default: m.Council })))
const PluginManager = lazy(() => import('@/pages/PluginManager').then((m) => ({ default: m.PluginManager })))
const PluginPage = lazy(() => import('@/pages/PluginPage').then((m) => ({ default: m.PluginPage })))
const CompanySettings = lazy(() => import('@/pages/CompanySettings').then((m) => ({ default: m.CompanySettings })))
const CompanyImportExport = lazy(() => import('@/pages/CompanyImportExport').then((m) => ({ default: m.CompanyImportExport })))
const SipocSetup = lazy(() => import('@/pages/SipocSetup').then((m) => ({ default: m.SipocSetup })))
const Audit = lazy(() => import('@/pages/Audit').then((m) => ({ default: m.Audit })))
const DesignPreview = lazy(() => import('@/pages/DesignPreview').then((m) => ({ default: m.DesignPreview })))
const Themes = lazy(() => import('@/pages/Themes').then((m) => ({ default: m.Themes })))
const AdminModels = lazy(() => import('@/pages/AdminModels').then((m) => ({ default: m.AdminModels })))
const AdminSpecialties = lazy(() => import('@/pages/AdminSpecialties').then((m) => ({ default: m.AdminSpecialties })))
const AdminConnectors = lazy(() => import('@/pages/AdminConnectors').then((m) => ({ default: m.AdminConnectors })))
const AdminMcp = lazy(() => import('@/pages/AdminMcp').then((m) => ({ default: m.AdminMcp })))
const AgentBuilderTest = lazy(() => import('@/pages/AgentBuilderTest').then((m) => ({ default: m.AgentBuilderTest })))
const CliAuth = lazy(() => import('@/pages/CliAuth').then((m) => ({ default: m.CliAuth })))
const InviteLanding = lazy(() => import('@/pages/InviteLanding').then((m) => ({ default: m.InviteLanding })))
const Intelligence = lazy(() => import('@/pages/Intelligence').then((m) => ({ default: m.Intelligence })))
const Prospects = lazy(() => import('@/pages/Prospects').then((m) => ({ default: m.Prospects })))
const ResearchTemplates = lazy(() => import('@/pages/ResearchTemplates').then((m) => ({ default: m.ResearchTemplates })))
const InstanceSettings = lazy(() => import('@/pages/InstanceSettings').then((m) => ({ default: m.InstanceSettings })))
const Login = lazy(() => import('@/pages/Login').then((m) => ({ default: m.Login })))
const NotFound = lazy(() => import('@/pages/NotFound').then((m) => ({ default: m.NotFound })))
const FlowLogic = lazy(() => import('@/pages/FlowLogic').then((m) => ({ default: m.FlowLogic })))
const Bpmn = lazy(() => import('@/pages/Bpmn').then((m) => ({ default: m.Bpmn })))
const BpmnEditor = lazy(() =>
  import('@/pages/BpmnEditor').then((m) => ({ default: m.BpmnEditor })),
)
const SipocProcessBpmn = lazy(() =>
  import('@/pages/SipocProcessBpmn').then((m) => ({ default: m.SipocProcessBpmn })),
)

function RouteFallback() {
  return <div className="p-6 text-sm text-muted-foreground">Carregando pagina...</div>
}

export function App() {
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Públicas */}
          <Route path="/login" element={<Login />} />

          {/* Protegidas — RequireAuth bloqueia durante hidratação da sessão */}
          <Route element={<RequireAuth />}>
            <Route element={<MainLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />

              {/* Rotas que exigem company ativa */}
              <Route element={<RequireCompany />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/agents" element={<Agents />} />
                <Route path="/agents/recommendations" element={<AgentRecommendations />} />
                <Route path="/agents/:id" element={<AgentDetail />} />
                <Route path="/agents/:id/workspace" element={<AgentWorkspace />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/inbox" element={<InboxPage />} />
                <Route path="/connectors/sessions" element={<ConnectorSessions />} />
                <Route path="/knowledge" element={<KnowledgeBase />} />
                <Route path="/goals" element={<Goals />} />
                <Route path="/goals/:id" element={<GoalDetail />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/pmo/dashboard" element={<PmoDashboard />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/workflow" element={<Workflow />} />
                <Route path="/workflow/ops" element={<WorkflowOps />} />
                <Route path="/prospects" element={<Prospects />} />
                <Route path="/research-templates" element={<ResearchTemplates />} />
                <Route path="/routines" element={<Routines />} />
                <Route path="/routines/new" element={<RoutineEditor />} />
                <Route path="/routines/:id" element={<RoutineEditor />} />
                <Route path="/runs/:id/transcript" element={<RunTranscript />} />
                <Route path="/analytics/cost" element={<CostAnalytics />} />
                <Route path="/settings/user" element={<UserSettings />} />
                <Route path="/sipoc/wizard" element={<SipocWizard />} />
                <Route path="/sipoc/wizard/:processId" element={<SipocWizard />} />
                <Route path="/sipoc/management" element={<SipocManagement />} />
                <Route path="/sipoc/processes/:processId/bpmn" element={<SipocProcessBpmn />} />
                <Route path="/sipoc/settings" element={<SipocSettings />} />
                <Route path="/sipoc/analytics" element={<SipocAnalytics />} />
                <Route path="/sipoc/report" element={<SipocReport />} />
                <Route path="/org" element={<Org />} />
                <Route path="/council" element={<Council />} />
                <Route path="/plugins" element={<PluginManager />} />
                <Route path="/plugins/:slug" element={<PluginPage />} />
                <Route path="/companies/:id/settings" element={<CompanySettings />} />
                <Route path="/companies/:id/export" element={<CompanyImportExport />} />
                <Route path="/flow-logic/:category" element={<FlowLogic />} />
                <Route path="/bpmn" element={<Bpmn />} />
                <Route path="/bpmn/new" element={<BpmnEditor />} />
                <Route path="/bpmn/:id" element={<BpmnEditor />} />
              </Route>

              {/* Rotas sem dependência de company */}
              <Route path="/sipoc/setup" element={<SipocSetup />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/design-preview" element={<DesignPreview />} />
              <Route path="/themes" element={<Themes />} />
              <Route path="/admin/models" element={<AdminModels />} />
              <Route path="/admin/specialties" element={<AdminSpecialties />} />
              <Route path="/admin/connectors" element={<AdminConnectors />} />
              <Route path="/admin/mcp" element={<AdminMcp />} />
              <Route path="/admin/agent-builder" element={<AgentBuilderTest />} />
              <Route path="/cli-auth" element={<CliAuth />} />
              <Route path="/invite/:token" element={<InviteLanding />} />
              <Route path="/intelligence" element={<Intelligence />} />
              <Route path="/settings/instance" element={<InstanceSettings />} />
              <Route path="/settings/instance/general" element={<InstanceSettings />} />
              <Route path="/settings/instance/experimental" element={<InstanceSettings />} />

              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      <ModeSwitcherPopover />
    </>
  )
}
